import React from 'react';

import { IEventJournalFiltersData } from '@entities/eventJournal/model/types';
import {
    useFilterByAuthor,
    useFilterByEstimate,
    useFilterByType,
    useResetFilters,
} from '@features/JournalEventsFilters/lib';
import { JournalEventsFiltersContextProvider } from '@features/JournalEventsFilters/providers/context';
import { generateId } from '@shared/helpers';
import { Skeleton } from '@shared/ui';

import cls from './JournalEventsFilters.module.css';
import { FilterByAuthor, FilterByEstimate, FilterByType, FilterTags } from './ui';

interface IJournalEventsFiltersProps {
    filtersData: IEventJournalFiltersData;
    isPending: boolean;
}

export const JournalEventsFilters = ({ filtersData, isPending }: IJournalEventsFiltersProps) => {
    const {
        data: authorsData,
        onAuthorClick,
        onClearValuesCb: onClearAuthorsValues,
        defaultValues: authorsDefaultValues,
    } = useFilterByAuthor({ authorsData: filtersData?.authors });
    const {
        data: estimatesData,
        onEstimateClick,
        onClearValuesCb: onClearEstimatesValues,
        defaultValues: estimatesDefaultValues,
    } = useFilterByEstimate({ estimatesData: filtersData?.estimates });
    const {
        data: typesData,
        onTypeClick,
        onClearValuesCb: onClearTypesValues,
        defaultValues: typesDefaultValues,
    } = useFilterByType({ typesData: filtersData?.types });

    const { onResetFilters, clearPeriodPickerQueryValue, clearDateRangeQueryValues } =
        useResetFilters();

    if (isPending) {
        return (
            <div className={cls.PendingWrapper}>
                {Array.from({ length: 4 }).map(() => (
                    <Skeleton
                        key={generateId(true)}
                        height={24}
                        border="8px"
                        classes={cls.SkeletonPicker}
                    />
                ))}
            </div>
        );
    }

    return (
        <JournalEventsFiltersContextProvider
            contextValues={{
                authorsData,
                estimatesData,
                typesData,
                onAuthorClick,
                onEstimateClick,
                onTypeClick,
                authorsDefaultValues,
                estimatesDefaultValues,
                typesDefaultValues,
                onClearAuthorsValues,
                onClearEstimatesValues,
                onClearTypesValues,
                onClearAllValues: onResetFilters,
                onDateClick: () => {
                    clearDateRangeQueryValues();
                    clearPeriodPickerQueryValue();
                },
            }}
        >
            <section className={cls.FiltersContainer}>
                <div className={cls.MultiSelectWrapper}>
                    <FilterByAuthor />
                    <FilterByEstimate />
                    <FilterByType />
                </div>
                <FilterTags />
            </section>
        </JournalEventsFiltersContextProvider>
    );
};
